import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Observable, Subject, combineLatest, of } from 'rxjs'
import { map, switchMap, takeUntil } from 'rxjs/operators'

import { FirestoreTosite, FirestoreTositteenKuva, PaymentStatus } from '@lemon/core-firebase/model/tosite'
import { TositeUriService } from '@lemon/core-firebase/service/tosite/tosite-uri.service'

import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { LEMONATOR_HTTPS_IMAGES_API } from 'app/_angular/service/lemon-http.service'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'

interface NaytettavaKuva extends FirestoreTositteenKuva {
  /** Kuvan latausosoite kuva-API:n läpi. Cookies on jo asetettu app-tasolla setup-kuva-cookie -kutsulla. */
  url: string
}

@Component({
  templateUrl: './ostolasku-selaa.katsele.component.html',
  styleUrls: ['./ostolasku-selaa.katsele.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OstolaskuSelaaKatseleComponent implements OnInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()
  private _tositeUriService = new TositeUriService()

  asiakas: Asiakas | null = null
  /** Ostolasku tallennetaan Firestoren kuitit-kokoelmaan, joten itse tieto-objekti on FirestoreTosite. */
  laskuObservable: Observable<FirestoreTosite | null>
  kuvatObservable: Observable<NaytettavaKuva[]>
  ostolaskuUriObservable: Observable<string>
  ostolaskuEncodedUriObservable: Observable<string>
  kirjanpitajaOnDevaajaObservable: Observable<boolean> = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _asiakasService: AsiakasService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _debugService: DebugService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
  ) {
    this.laskuObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this._route.paramMap]).pipe(
      switchMap(([avainTiedot, params]) => {
        const laskuAvain = params.get('laskuAvain')
        if (!avainTiedot?.avain || !laskuAvain) {
          return of<FirestoreTosite | null>(null)
        }
        const uri = this._tositeUriService.annaTositteenFirestoreUri(avainTiedot.avain, laskuAvain)
        return this._firebaseLemonaid
          .firestoreDoc<FirestoreTosite>(uri)
          .listen()
          .pipe(
            map(doc => {
              if (!doc) {
                return null
              }
              doc.avain = laskuAvain
              return doc
            }),
          )
      }),
    )

    this.kuvatObservable = combineLatest([this.laskuObservable, this._asiakasService.nykyinenAsiakasAvainObservable]).pipe(
      map(([lasku, avainTiedot]) => {
        if (!lasku || !avainTiedot?.asiakasId || !lasku.kuvat) {
          return []
        }
        return this._annaKuvat(lasku, avainTiedot.asiakasId + '')
      }),
    )
  }

  ngOnInit() {
    this._asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asiakas => {
      this.asiakas = asiakas
    })

    this.ostolaskuUriObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this._route.paramMap]).pipe(
      map(([avainTiedot, params]) => {
        const laskuAvain = params.get('laskuAvain')
        if (avainTiedot?.avain && laskuAvain) {
          return this._tositeUriService.annaTositteenFirestoreUri(avainTiedot.avain, laskuAvain)
        }
        return ''
      }),
    )

    this.ostolaskuEncodedUriObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this._route.paramMap]).pipe(
      map(([avainTiedot, params]) => {
        const laskuAvain = params.get('laskuAvain')
        if (avainTiedot?.avain && laskuAvain) {
          const uri = this._tositeUriService.annaTositteenFirestoreUri(avainTiedot.avain, laskuAvain)
          return this._debugService.createFirestoreLinkLemonaid(uri)
        }
        return ''
      }),
    )
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  takaisin() {
    if (!this.asiakas?.avain) {
      return
    }
    this._router.navigate(['asiakkaat', this.asiakas.avain, 'ostolaskut', 'selaa'])
  }

  paymentStatusNimi(status: PaymentStatus | undefined): string {
    switch (status) {
      case PaymentStatus.AVOIN:
        return 'Avoin'
      case PaymentStatus.ERAANTYNYT:
        return 'Erääntynyt'
      case PaymentStatus.MAKSUSSA:
        return 'Maksussa'
      case PaymentStatus.MAKSETTU:
        return 'Maksettu'
      case PaymentStatus.MAKSETTU_TOISAALLA:
        return 'Maksettu toisaalla'
      case PaymentStatus.PANKKI_HYLKASI:
        return 'Pankki hylkäsi'
      case PaymentStatus.ASIAKAS_HYLKASI:
        return 'Asiakas hylkäsi'
      case PaymentStatus.KASITELTY:
        return 'Käsitelty'
      default:
        return '–'
    }
  }

  tyyppiNimi(tyyppi: FirestoreTosite['type'] | undefined): string {
    switch (tyyppi) {
      case 'einvoice':
        return 'Sähköinen lasku'
      case 'payment':
        return 'Maksu'
      case 'tosite':
        return 'Tosite'
      default:
        return '–'
    }
  }

  /**
   * Rakentaa näytettävien kuvien listan ostolaskun `kuvat`-map:istä. Käyttää samaa kuva-API-polkua
   * kuin lemonator-puolen olemassa oleva tosite.katsele — Lemonatorin kuva-CDN palauttaa
   * webp-muotoisen quality-version cookies-tunnistuksella (cookies asetettu app-tasolla).
   */
  private _annaKuvat(lasku: FirestoreTosite, asiakasId: string): NaytettavaKuva[] {
    const tulos: NaytettavaKuva[] = []
    for (const avain of Object.keys(lasku.kuvat)) {
      const kuva = lasku.kuvat[avain]
      if (kuva.poistettu) {
        continue
      }
      const kuvaAvainQuality = kuva.avain + '_quality'
      const kuvanUrl = this._tositeUriService.annaCloudStorageKuvaUri(asiakasId, lasku.kuvakansio, kuvaAvainQuality, 'webp')
      tulos.push({ ...kuva, url: LEMONATOR_HTTPS_IMAGES_API + '/api/1/kuvat/' + kuvanUrl })
    }
    return tulos.sort((a, b) => (a.jarjestys ?? 0) - (b.jarjestys ?? 0))
  }
}
